import { useEffect, useMemo, useState } from "react";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import TuneIcon from "@mui/icons-material/Tune";
import AddIcon from "@mui/icons-material/Add";
import { useSelector } from "react-redux";
import { selectUser } from "../../features/userSlice";
import { selectRoles } from "../../features/roleSlice";
import {
  addQuoteCollaborator,
  getCabinetAdminContext,
  getCabinetQuotes,
  getQuoteCollaborators,
  removeQuoteCollaborator,
  updateCabinetFormTemplate,
  updateQuoteCollaboratorPermission,
} from "./api";
import { CABINET_COLLABORATOR_PERMISSIONS, CABINET_QUOTE_STATUSES, normalizeCabinetQuoteStatuses } from "./constants";
import { CQ_FIELD, CQ_FIELD_SM, Notice } from "./ui";
import { PageHeader, PageShell } from "../../components/UI/Page";

const CARD = "app-card space-y-4 rounded-2xl border p-5";
const CARD_TITLE = "text-lg font-semibold tracking-tight";
const BTN_PRIMARY = "app-btn app-btn-primary";
const BTN_SECONDARY = "app-btn app-btn-secondary";

const randomConfigId = (prefix) => `${prefix}_${Math.random().toString(36).slice(2, 10)}`;

const fieldsToText = (fields) =>
  (Array.isArray(fields) ? fields : [])
    .map((field) => String(field?.label || "").trim())
    .filter(Boolean)
    .join("\n");

const textToFields = (text) =>
  String(text || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((label, index) => ({
      id: `checklist_${index + 1}`,
      label,
      type: "text",
      required: false,
    }));

const createField = () => ({
  id: randomConfigId("field"),
  label: "New Field",
  type: "text",
  required: false,
  options: [],
});

const createSection = (index) => ({
  id: randomConfigId("section"),
  title: `Section ${index + 1}`,
  columns: 2,
  repeatable: false,
  fields: [createField()],
});

const createTemplate = (index) => ({
  id: `template_${index + 1}`,
  name: `Template ${index + 1}`,
  description: "",
  sections: [createSection(0)],
});

function CabinetAdmin() {
  const user = useSelector(selectUser);
  const roleState = useSelector(selectRoles);
  const [context, setContext] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [selectedQuoteId, setSelectedQuoteId] = useState("");
  const [collabState, setCollabState] = useState({ owner_user_id: "", owner_name: "", collaborators: [] });
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const [permission, setPermission] = useState(CABINET_COLLABORATOR_PERMISSIONS[0]);
  const [templateText, setTemplateText] = useState("");
  const [formTemplates, setFormTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [editingOptions, setEditingOptions] = useState(null);
  const [modalOptionsText, setModalOptionsText] = useState("");
  const [quoteStatuses, setQuoteStatuses] = useState([...CABINET_QUOTE_STATUSES]);
  const [newStatusValue, setNewStatusValue] = useState("");
  const [error, setError] = useState("");
  const [saveMessage, setSaveMessage] = useState("");

  const roles = roleState?.roles || [];

  useEffect(() => {
    if (!user?.userID) return;

    getCabinetAdminContext(user.userID, roles)
      .then((response) => {
        setContext(response);
        setTemplateText(fieldsToText(response?.formConfig?.fields || []));
        const templates = Array.isArray(response?.formConfig?.templates) ? response.formConfig.templates : [];
        setFormTemplates(templates);
        setSelectedTemplateId(templates[0]?.id || "");
        setQuoteStatuses(normalizeCabinetQuoteStatuses(response?.formConfig?.quote_statuses));
      })
      .catch((loadError) => setError(loadError?.message || "Unable to load cabinet admin context"));

    getCabinetQuotes({ userId: user.userID, sortBy: "updated_at", sortDirection: "desc" }).then((response) => {
      setQuotes(Array.isArray(response) ? response : []);
    });
  }, [user, roles]);

  useEffect(() => {
    if (!selectedQuoteId || !user?.userID) {
      setCollabState({ owner_user_id: "", owner_name: "", collaborators: [] });
      return;
    }

    getQuoteCollaborators(selectedQuoteId, user.userID)
      .then((response) => setCollabState(response))
      .catch((loadError) => setError(loadError?.message || "Unable to load collaborators for quote"));
  }, [selectedQuoteId, user]);

  const availableEmployees = useMemo(() => {
    const existing = new Set((collabState.collaborators || []).map((item) => item.user_id));
    existing.add(collabState.owner_user_id);
    return (context?.employees || []).filter((employee) => employee?.id && !existing.has(employee.id));
  }, [context, collabState]);

  const onSaveFormTemplates = async () => {
    setError("");
    setSaveMessage("");
    try {
      const updated = await updateCabinetFormTemplate(
        user?.userID,
        {
          fields: context?.formConfig?.fields || textToFields(templateText),
          templates: formTemplates,
          quote_statuses: quoteStatuses,
        },
        roles
      );
      setContext((prev) => ({ ...prev, formConfig: updated }));
      setFormTemplates(Array.isArray(updated?.templates) ? updated.templates : []);
      setQuoteStatuses(normalizeCabinetQuoteStatuses(updated?.quote_statuses || quoteStatuses));
      setSaveMessage("Form templates saved.");
    } catch (saveError) {
      setError(saveError?.message || "Unable to save form templates");
    }
  };

  const onSaveChecklistTemplate = async () => {
    setError("");
    setSaveMessage("");
    const fields = textToFields(templateText);
    if (fields.length === 0) {
      setError("At least one checklist template line is required.");
      return;
    }

    try {
      const updated = await updateCabinetFormTemplate(user?.userID, { fields, templates: formTemplates, quote_statuses: quoteStatuses }, roles);
      setContext((prev) => ({ ...prev, formConfig: updated }));
      setFormTemplates(Array.isArray(updated?.templates) ? updated.templates : []);
      setQuoteStatuses(normalizeCabinetQuoteStatuses(updated?.quote_statuses || quoteStatuses));
      setSaveMessage("Checklist template and form templates saved.");
    } catch (saveError) {
      setError(saveError?.message || "Unable to save checklist template");
    }
  };

  const onAddFormTemplate = () => {
    const newTemplate = createTemplate(formTemplates.length);
    setFormTemplates((prev) => [...prev, newTemplate]);
    setSelectedTemplateId(newTemplate.id);
  };

  const onDuplicateFormTemplate = (templateId) => {
    if (!templateId) return;
    let duplicatedTemplateId = "";
    setFormTemplates((prev) => {
      const sourceIndex = prev.findIndex((template) => template.id === templateId);
      if (sourceIndex < 0) return prev;
      const sourceTemplate = prev[sourceIndex];
      const duplicateTemplate = {
        ...sourceTemplate,
        id: randomConfigId("template"),
        name: `${sourceTemplate?.name || "Template"} Copy`,
        sections: (sourceTemplate.sections || []).map((section) => ({
          ...section,
          id: randomConfigId("section"),
          fields: (section.fields || []).map((field) => ({
            ...field,
            id: randomConfigId("field"),
          })),
        })),
      };
      duplicatedTemplateId = duplicateTemplate.id;

      return [...prev.slice(0, sourceIndex + 1), duplicateTemplate, ...prev.slice(sourceIndex + 1)];
    });
    setSelectedTemplateId(duplicatedTemplateId || templateId);
  };

  const onAddSection = (templateIndex) => {
    setFormTemplates((prev) =>
      prev.map((template, index) => {
        if (index !== templateIndex) return template;
        return { ...template, sections: [...(template.sections || []), createSection((template.sections || []).length)] };
      })
    );
  };

  const onDuplicateSection = (templateIndex, sectionIndex) => {
    setFormTemplates((prev) =>
      prev.map((template, tIndex) => {
        if (tIndex !== templateIndex) return template;
        const sections = template.sections || [];
        const sourceSection = sections[sectionIndex];
        if (!sourceSection) return template;

        const duplicatedSection = {
          ...sourceSection,
          id: randomConfigId("section"),
          title: `${sourceSection?.title || `Section ${sectionIndex + 1}`} Copy`,
          fields: (sourceSection.fields || []).map((field) => ({
            ...field,
            id: randomConfigId("field"),
          })),
        };

        return {
          ...template,
          sections: [...sections.slice(0, sectionIndex + 1), duplicatedSection, ...sections.slice(sectionIndex + 1)],
        };
      })
    );
  };


  const onRemoveSection = (templateIndex, sectionIndex) => {
    setFormTemplates((prev) =>
      prev.map((template, tIndex) => {
        if (tIndex !== templateIndex) return template;
        const sections = template.sections || [];
        const nextSections = sections.filter((_, index) => index !== sectionIndex);
        return { ...template, sections: nextSections.length > 0 ? nextSections : [createSection(0)] };
      })
    );
  };

  const onAddField = (templateIndex, sectionIndex) => {
    setFormTemplates((prev) =>
      prev.map((template, tIndex) => {
        if (tIndex !== templateIndex) return template;
        const sections = (template.sections || []).map((section, sIndex) =>
          sIndex === sectionIndex ? { ...section, fields: [...(section.fields || []), createField()] } : section
        );
        return { ...template, sections };
      })
    );
  };

  const onRemoveField = (templateIndex, sectionIndex, fieldIndex) => {
    setFormTemplates((prev) =>
      prev.map((template, tIndex) => {
        if (tIndex !== templateIndex) return template;
        const sections = (template.sections || []).map((section, sIndex) => {
          if (sIndex !== sectionIndex) return section;
          const nextFields = (section.fields || []).filter((_, index) => index !== fieldIndex);
          return { ...section, fields: nextFields.length > 0 ? nextFields : [createField()] };
        });
        return { ...template, sections };
      })
    );
  };

  const onTemplateChange = (templateIndex, key, value) => {
    setFormTemplates((prev) => prev.map((template, index) => (index === templateIndex ? { ...template, [key]: value } : template)));
  };

  const selectedTemplate = useMemo(
    () => (formTemplates || []).find((template) => template.id === selectedTemplateId) || (formTemplates || [])[0] || null,
    [formTemplates, selectedTemplateId]
  );

  const selectedTemplateIndex = useMemo(
    () => (formTemplates || []).findIndex((template) => template.id === selectedTemplate?.id),
    [formTemplates, selectedTemplate]
  );

  const onSectionChange = (templateIndex, sectionIndex, key, value) => {
    setFormTemplates((prev) =>
      prev.map((template, tIndex) => {
        if (tIndex !== templateIndex) return template;
        const sections = (template.sections || []).map((section, sIndex) => (sIndex === sectionIndex ? { ...section, [key]: value } : section));
        return { ...template, sections };
      })
    );
  };

  const onFieldChange = (templateIndex, sectionIndex, fieldIndex, key, value) => {
    setFormTemplates((prev) =>
      prev.map((template, tIndex) => {
        if (tIndex !== templateIndex) return template;
        const sections = (template.sections || []).map((section, sIndex) => {
          if (sIndex !== sectionIndex) return section;
          const fields = (section.fields || []).map((field, fIndex) => (fIndex === fieldIndex ? { ...field, [key]: value } : field));
          return { ...section, fields };
        });
        return { ...template, sections };
      })
    );
  };

  const openOptionsModal = (templateIndex, sectionIndex, fieldIndex, options) => {
    setEditingOptions({ templateIndex, sectionIndex, fieldIndex });
    setModalOptionsText(Array.isArray(options) ? options.join("\n") : "");
  };

  const saveOptionsModal = () => {
    if (!editingOptions) return;
    const normalizedOptions = modalOptionsText
      .split("\n")
      .map((item) => item.trim())
      .filter(Boolean);
    onFieldChange(editingOptions.templateIndex, editingOptions.sectionIndex, editingOptions.fieldIndex, "options", normalizedOptions);
    setEditingOptions(null);
    setModalOptionsText("");
  };

  const onAddCollaborator = async () => {
    setError("");
    if (!selectedQuoteId || !selectedEmployeeId) return;
    try {
      await addQuoteCollaborator(selectedQuoteId, user?.userID, { user_id: selectedEmployeeId, permission });
      const updated = await getQuoteCollaborators(selectedQuoteId, user?.userID);
      setCollabState(updated);
      setSelectedEmployeeId("");
    } catch (submitError) {
      setError(submitError?.message || "Unable to add collaborator");
    }
  };

  const onUpdatePermission = async (collaboratorUserId, nextPermission) => {
    setError("");
    try {
      await updateQuoteCollaboratorPermission(selectedQuoteId, user?.userID, collaboratorUserId, nextPermission);
      const updated = await getQuoteCollaborators(selectedQuoteId, user?.userID);
      setCollabState(updated);
    } catch (submitError) {
      setError(submitError?.message || "Unable to update permission");
    }
  };

  const onRemoveCollaborator = async (collaboratorUserId) => {
    setError("");
    try {
      await removeQuoteCollaborator(selectedQuoteId, user?.userID, collaboratorUserId);
      const updated = await getQuoteCollaborators(selectedQuoteId, user?.userID);
      setCollabState(updated);
    } catch (submitError) {
      setError(submitError?.message || "Unable to remove collaborator");
    }
  };

  const onAddQuoteStatus = () => {
    const trimmed = String(newStatusValue || "").trim();
    if (!trimmed) {
      return;
    }

    setQuoteStatuses((prev) => normalizeCabinetQuoteStatuses([...(prev || []), trimmed]));
    setNewStatusValue("");
  };

  const onRemoveQuoteStatus = (statusToRemove) => {
    setQuoteStatuses((prev) => {
      const next = (prev || []).filter((status) => status !== statusToRemove);
      return normalizeCabinetQuoteStatuses(next.length > 0 ? next : CABINET_QUOTE_STATUSES);
    });
  };

  return (
    <PageShell size="xl">
      <PageHeader
        icon={AdminPanelSettingsIcon}
        tone="amber"
        eyebrow="Cabinets"
        title="Cabinet Admin"
        description="For IT/Admin users: manage quote templates, checklist defaults, and collaborators."
      />

      <div className="space-y-6">
      {error && <Notice tone="rose" role="alert">{error}</Notice>}
      {saveMessage && <Notice tone="emerald" role="status">{saveMessage}</Notice>}

      <section className={CARD}>
        <div>
          <h2 className={CARD_TITLE}>Quote Statuses</h2>
          <p className="app-muted text-sm">Add and maintain the statuses available in cabinet quotes.</p>
        </div>

        <div className="flex flex-col gap-2 md:flex-row">
          <input
            className={`${CQ_FIELD} flex-1`}
            aria-label="New status"
            value={newStatusValue}
            onChange={(event) => setNewStatusValue(event.target.value)}
            placeholder="Add a new status (example: waiting_install)"
          />
          <button className={BTN_SECONDARY} type="button" onClick={onAddQuoteStatus}>Add Status</button>
        </div>

        <div className="flex flex-wrap gap-2">
          {quoteStatuses.map((status) => (
            <button
              key={status}
              type="button"
              className="app-chip min-h-[2rem] gap-1.5 px-3 text-sm transition-colors hover:border-rose-500/40 hover:bg-rose-500/10 hover:text-rose-700 dark:hover:text-rose-300"
              onClick={() => onRemoveQuoteStatus(status)}
              title="Click to remove status"
              aria-label={`Remove status ${status}`}
            >
              {status} ✕
            </button>
          ))}
        </div>
        <p className="app-muted text-xs">Use “Save Form Templates” below to persist status changes.</p>
      </section>

      <section className={CARD}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className={CARD_TITLE}>Custom Quote Form Templates</h2>
          <div className="flex flex-wrap items-center gap-2">
            <button className={BTN_SECONDARY} type="button" onClick={onAddFormTemplate}>Add Template</button>
            <button className={BTN_PRIMARY} type="button" onClick={onSaveFormTemplates}>Save Form Templates</button>
          </div>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <label htmlFor="cabinet-admin-template" className="flex items-center gap-2 text-sm font-medium">
            <SwapHorizIcon fontSize="small" className="app-muted" />
            Template
          </label>
          <div className="flex flex-1 flex-wrap items-center gap-2">
            <select
              id="cabinet-admin-template"
              className={`${CQ_FIELD} min-w-0 flex-1 sm:max-w-xs`}
              value={selectedTemplate?.id || ""}
              onChange={(event) => setSelectedTemplateId(event.target.value)}
            >
              {(formTemplates || []).map((template) => (
                <option key={template.id} value={template.id}>{template.name || template.id}</option>
              ))}
            </select>
            <button
              className={BTN_SECONDARY}
              type="button"
              disabled={!selectedTemplate}
              onClick={() => onDuplicateFormTemplate(selectedTemplate?.id)}
            >
              <ContentCopyIcon fontSize="small" />
              Duplicate
            </button>
          </div>
        </div>

        {selectedTemplate && (
          <div key={selectedTemplate.id || selectedTemplateIndex} className="app-surface space-y-4 rounded-xl border p-4">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <input
                className={CQ_FIELD}
                aria-label="Template name"
                value={selectedTemplate.name || ""}
                onChange={(event) => onTemplateChange(selectedTemplateIndex, "name", event.target.value)}
                placeholder="Template name"
              />
              <input
                className={CQ_FIELD}
                aria-label="Template description"
                value={selectedTemplate.description || ""}
                onChange={(event) => onTemplateChange(selectedTemplateIndex, "description", event.target.value)}
                placeholder="Description"
              />
            </div>

            {(selectedTemplate.sections || []).map((section, sectionIndex) => (
              <div key={section.id || sectionIndex} className="app-card space-y-3 rounded-xl border p-3 sm:p-4">
                <div className="grid grid-cols-1 items-center gap-2 md:grid-cols-[minmax(0,2fr)_6rem_auto_auto_auto]">
                  <input className={CQ_FIELD} aria-label="Section title" value={section.title || ""} onChange={(event) => onSectionChange(selectedTemplateIndex, sectionIndex, "title", event.target.value)} placeholder="Section title" />
                  <input className={CQ_FIELD} aria-label="Columns" title="Columns" type="number" min="1" max="7" value={section.columns || 1} onChange={(event) => onSectionChange(selectedTemplateIndex, sectionIndex, "columns", Number(event.target.value || 1))} placeholder="Columns" />
                  <label className="flex items-center gap-2 px-1 text-sm">
                    <input type="checkbox" className="h-4 w-4 accent-sky-600" checked={Boolean(section.repeatable)} onChange={(event) => onSectionChange(selectedTemplateIndex, sectionIndex, "repeatable", event.target.checked)} />
                    Repeatable rows
                  </label>
                  <button
                    className="app-btn app-btn-secondary app-btn-sm"
                    type="button"
                    onClick={() => onDuplicateSection(selectedTemplateIndex, sectionIndex)}
                  >
                    <ContentCopyIcon fontSize="small" />
                    Duplicate section
                  </button>
                  <button
                    className="app-btn app-btn-ghost app-btn-sm !text-rose-700 hover:!bg-rose-500/10 dark:!text-rose-300"
                    type="button"
                    onClick={() => onRemoveSection(selectedTemplateIndex, sectionIndex)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                    Delete section
                  </button>
                </div>

                <div className="app-divider space-y-2 border-t pt-3">
                {(section.fields || []).map((field, fieldIndex) => (
                  <div key={field.id || fieldIndex} className="space-y-2">
                    <div className="grid grid-cols-[2.5rem_minmax(0,1fr)] items-center gap-2 md:grid-cols-[2.5rem_minmax(0,2fr)_minmax(0,1fr)_minmax(0,1.2fr)_auto]">
                      <button
                        className="app-icon-btn hover:!bg-rose-500/10 hover:!text-rose-600 dark:hover:!text-rose-300"
                        title="Remove field"
                        aria-label="Remove field"
                        type="button"
                        onClick={() => onRemoveField(selectedTemplateIndex, sectionIndex, fieldIndex)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </button>
                      <input className={CQ_FIELD_SM} aria-label="Field label" value={field.label || ""} onChange={(event) => onFieldChange(selectedTemplateIndex, sectionIndex, fieldIndex, "label", event.target.value)} placeholder="Field label" />
                      <select className={`${CQ_FIELD_SM} col-start-2 md:col-start-auto`} aria-label="Field type" value={field.type || "text"} onChange={(event) => onFieldChange(selectedTemplateIndex, sectionIndex, fieldIndex, "type", event.target.value)}>
                        <option value="text">Text</option>
                        <option value="select">Dropdown</option>
                      </select>
                      <div className="col-start-2 md:col-start-auto">
                        {field.type === "select" && (
                          <button
                            className="app-btn app-btn-secondary app-btn-sm w-full"
                            type="button"
                            onClick={() => openOptionsModal(selectedTemplateIndex, sectionIndex, fieldIndex, field.options)}
                          >
                            <TuneIcon sx={{ fontSize: 16 }} />
                            Dropdown Values
                          </button>
                        )}
                      </div>
                      <label className="col-start-2 flex items-center gap-2 px-1 text-sm md:col-start-auto">
                        <input type="checkbox" className="h-4 w-4 accent-sky-600" checked={Boolean(field.required)} onChange={(event) => onFieldChange(selectedTemplateIndex, sectionIndex, fieldIndex, "required", event.target.checked)} />
                        Required
                      </label>
                    </div>
                  </div>
                ))}
                </div>
                <button className="app-btn app-btn-ghost app-btn-sm app-accent" type="button" onClick={() => onAddField(selectedTemplateIndex, sectionIndex)}><AddIcon sx={{ fontSize: 18 }} />Add Field</button>
              </div>
            ))}
            <button className={`${BTN_SECONDARY} app-btn-sm`} type="button" onClick={() => onAddSection(selectedTemplateIndex)}><AddIcon sx={{ fontSize: 18 }} />Add Section</button>
          </div>
        )}
        {!selectedTemplate && <p className="app-muted text-sm">No form templates added yet.</p>}
      </section>

      <section className={CARD}>
        <div>
          <h2 className={CARD_TITLE}>Default Checklist Template</h2>
          <p className="app-muted text-sm">One checklist line per row. This is the default template used on quote workspaces.</p>
        </div>
        <textarea className={`${CQ_FIELD} min-h-[120px]`} aria-label="Default checklist template" value={templateText} onChange={(event) => setTemplateText(event.target.value)} placeholder="Enter one checklist field per line" />
        <button className={BTN_PRIMARY} type="button" onClick={onSaveChecklistTemplate}>Save Default Checklist Template</button>
      </section>

      <section className={CARD}>
        <h2 className={CARD_TITLE}>Collaborator Management</h2>
        <select className={CQ_FIELD} aria-label="Select quote" value={selectedQuoteId} onChange={(event) => setSelectedQuoteId(event.target.value)}>
          <option value="">Select quote</option>
          {quotes.map((quote) => (
            <option key={quote._id} value={quote._id}>{quote.quote_number || quote._id} - {quote.title || "Untitled"}</option>
          ))}
        </select>

        {selectedQuoteId && (
          <>
            <div className="app-surface rounded-xl border px-3 py-2 text-sm"><strong>Owner:</strong> {collabState.owner_name || collabState.owner_user_id}</div>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
              <select className={CQ_FIELD} aria-label="Select employee" value={selectedEmployeeId} onChange={(event) => setSelectedEmployeeId(event.target.value)}>
                <option value="">Select employee</option>
                {availableEmployees.map((employee) => (<option key={employee.id} value={employee.id}>{employee.name || employee.id}</option>))}
              </select>
              <select className={CQ_FIELD} aria-label="Permission" value={permission} onChange={(event) => setPermission(event.target.value)}>
                {CABINET_COLLABORATOR_PERMISSIONS.map((level) => (<option key={level} value={level}>{level}</option>))}
              </select>
              <button className={BTN_PRIMARY} type="button" onClick={onAddCollaborator}>Add Collaborator</button>
            </div>
            <ul className="space-y-2 text-sm">
              {(collabState.collaborators || []).map((person) => (
                <li key={person.user_id} className="app-surface flex flex-wrap items-center justify-between gap-2 rounded-xl border px-3 py-2">
                  <div className="min-w-0">
                    <p className="font-medium">{person.display_name || person.user_id}</p>
                    <p className="app-muted text-xs">{person.user_id}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <select className={CQ_FIELD_SM} aria-label={`Permission for ${person.display_name || person.user_id}`} value={person.permission} onChange={(event) => onUpdatePermission(person.user_id, event.target.value)}>
                      {CABINET_COLLABORATOR_PERMISSIONS.map((level) => (<option key={level} value={level}>{level}</option>))}
                    </select>
                    <button className="app-btn app-btn-sm bg-rose-500/10 text-rose-700 hover:bg-rose-500/20 dark:text-rose-300" type="button" onClick={() => onRemoveCollaborator(person.user_id)}>Remove</button>
                  </div>
                </li>
              ))}
              {(collabState.collaborators || []).length === 0 && <li className="app-muted">No collaborators assigned.</li>}
            </ul>
          </>
        )}
      </section>
      </div>

      {editingOptions && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 glass:backdrop-blur-[6px]"
          style={{ backgroundColor: "var(--app-scrim)" }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="cabinet-admin-options-title"
            className="app-glass app-glass-rim w-full max-w-lg space-y-4 rounded-3xl border p-6"
          >
            <div>
              <h3 id="cabinet-admin-options-title" className="text-lg font-semibold">Edit Dropdown Values</h3>
              <p className="app-muted text-sm">Enter one option per line.</p>
            </div>
            <textarea className={`${CQ_FIELD} min-h-[180px]`} aria-label="Dropdown values" value={modalOptionsText} onChange={(event) => setModalOptionsText(event.target.value)} />
            <div className="flex justify-end gap-2">
              <button className="app-btn app-btn-ghost" type="button" onClick={() => setEditingOptions(null)}>Cancel</button>
              <button className={BTN_PRIMARY} type="button" onClick={saveOptionsModal}>Save Values</button>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
}

export default CabinetAdmin;
